import React from 'react';
import { getEffectIcon, computeEffectData } from '../GoldenEffectBanner';
import { getDrugIconPaths, getKeystoneIconPath } from '../../data/keystoneCards';
import { getCardLedger } from '../../data/cardLedgers';
import { renderStatusText } from '../CombatPlayerbar/statusWordGlyphs';
import { MechGlyph } from '../GameTip/glyphs';
import KeystoneBeatAnimation from './KeystoneBeatAnimation';
import KeystoneHealAnimation from './KeystoneHealAnimation';
import CanonicalEffectChip from './CanonicalEffectChip';
import DrugInfluenceRows from './DrugInfluenceRows';
import { drugInfluenceRows } from '../../utils/htnBlister';
import { isHtnDrugTooltipRowsEnabled } from '../../utils/envFlags';
import './PillCard.css';

// Render the drug's skill-tree NODE glyph so a compact gold card's face icon
// matches the SAME drug's node on the skill tree — identical paths
// (getDrugIconPaths, the source ProgressMap MapNode draws) and identical stroke
// styling (fill none, 1.8 stroke, round caps/joins, 24x24 canvas). Color is
// inherited from the icon-ring via currentColor, like the effect icon it
// replaces. Returns null when the drug has no node glyph so PillCard can fall
// back to the effect icon. Edward 2026-06-09 ("make the card icons match the
// skill-tree node SVG styling").
function drugNodeGlyph(card) {
  const raw = card?.drug_slug || card?.slug || card?.card_slug || card?.generic_name || '';
  const slug = String(raw).toLowerCase().trim().replace(/[\s-]+/g, '_');
  const paths = getDrugIconPaths(slug);
  if (!Array.isArray(paths) || paths.length === 0) return null;
  return (
    <svg
      className="geb-icon"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}

/**
 * PillCard — ultra-compact golden card.
 *
 * Face: icon, ability name, drug name, drug class
 * Hover: swaps face for description, pros/cons, mechanics, requirement
 * Action button always visible below (never covered by hover).
 */
export default function PillCard({
  card,
  variant = 'default',
  onClick,
  actionButton,
  footAction = null,      // optional JSX rendered INLINE in the foot, between type and cost
  showCost = true,
  equipped = false,
  owned = false,
  requirement,
  iconOverride = null,    // optional JSX to replace the effect icon
  typeLabel = null,       // optional string to replace the effectType label
  typeMode = null,        // optional class suffix (e.g. 'gold') for the type tag color
  hideType = false,       // when true, suppress the foot type/status tag entirely
                          // (skill-tree toast: node state already conveys status)
  accentColor = null,     // optional CSS color to override --pill-accent (stripe + ring)
  disableHover = false,   // when true, face stays visible and hover content is suppressed
  sideDetails = false,    // when true, the face stays visible and the effect details
                          // slide OUT to the side on hover (keeps the compact card a
                          // fixed size) instead of swapping the face in place
  sideFlip = false,       // with sideDetails: extend the panel to the LEFT instead of
                          // the right (caller flips it when the right edge has no room)
  keystoneBonus = null,   // optional {keystone, color, lines[], active} — the card's
                          // keystone-synergy bonus, rendered as a dedicated section in
                          // the side panel (mirrors the combat bar's synergy section)
  tooltipOnly = false,    // when true, render ONLY the "what it does" panel as a
                          // standalone tooltip (no card face / foot / action). The equip
                          // hotbar uses this — it's a pure-info surface, so it shows the
                          // tooltip outright; the compact gold ACTION card stays on the
                          // skill tree. (Edward 2026-06-25)
  keystoneSections = false, // with tooltipOnly: split the body into TWO sections —
                          // top = how the beats work (effect.beatsBody), bottom = the
                          // concrete game effects (effect.effectsBody) where the green/red
                          // pro/con tags live. Keystone tooltips only. (Edward 2026-06-26)
  // ── Reconciled node-toast FLOAT layout (design port, Edward 2026-07-09) ──
  // Opt-in structural variant used ONLY by the desktop skill-tree node-click
  // float (.stn-toast--float). Renders the design's split header (effect │
  // generic) + relocated drug-class + centered body + synergy-name lift +
  // 4 math-glyph footer buttons + dual-lens header handles. Every OTHER
  // PillCard surface leaves this false and renders byte-identically to before.
  sideDetailsLayout = false,
  footButtons = null,       // JSX: the 4 math-glyph footer buttons (built by the toast,
                            // which owns the equip/unequip/close/synergy handlers)
  lens = 'effect',          // 'effect' | 'drug' — dual-lens current view
  lensEnabled = false,      // header labels are interactive ONLY when a drug lens exists
  lensSwapping = false,     // drives the .is-lens-swapping cross-fade
  onLensSelect = null,      // (which) => void, fired by the header lens handles
  drugLensDesc = null,      // clinical/pharmacology copy shown in the drug lens
}) {
  if (!card) return null;

  const effect = card.effect;
  // disableHover cards (e.g. the LoadoutDraftModal demo draft) suppress the hover
  // body entirely, but .pill-card__content still reserves the taller swap layer —
  // so injecting the authored rows there would silently grow the static card and
  // disturb the draft/flip layout without ever showing the rows. Skip them.
  const authoredInfluenceRows = isHtnDrugTooltipRowsEnabled() && !disableHover
    ? drugInfluenceRows(card)
    : [];
  const authoredInfluenceBlock = authoredInfluenceRows.length > 0
    ? <DrugInfluenceRows rows={authoredInfluenceRows} drug={card} />
    : null;
  // An HTN starter drug node-toast: its body is ONLY the authored influence
  // rows + the "What it does" side-head (the authored reference has no effect
  // description paragraph, no legacy pros/cons chips, no duplicate side title).
  // Scoped to cards that actually emit influence rows, so every other PillCard
  // surface (incl. the finalized keystone cards, which emit none) is untouched.
  const isHtnDrugToast = authoredInfluenceRows.length > 0;
  const effectType = effect?.type || 'combat';
  const { pros, filteredCons, mechanics } = effect
    ? computeEffectData(effect)
    : { pros: [], filteredCons: [], mechanics: [] };

  const drugClass = card.drug_class || '';
  const typeText = typeLabel || effectType;
  const typeClass = typeMode || effectType;

  const classes = [
    'pill-card',
    `pill-card--${effectType}`,
    variant !== 'default' && `pill-card--${variant}`,
    onClick && 'pill-card--clickable',
    disableHover && 'pill-card--no-hover',
    sideDetails && 'pill-card--side-details',
    sideDetails && sideFlip && 'pill-card--side-left',
    sideDetailsLayout && 'pill-card--side-details-layout',
    // Keystone variant of the side-details float: widens the card + stacks its two
    // sections (.ks-split) so the keystone detail rides the drug node-toast chrome.
    sideDetailsLayout && keystoneSections && 'pill-card--keystone',
    sideDetailsLayout && lens === 'drug' && 'is-drug-lens',
    sideDetailsLayout && lensSwapping && 'is-lens-swapping',
  ].filter(Boolean).join(' ');

  const rootStyle = accentColor ? { '--pill-accent': accentColor } : undefined;

  // CONSOLIDATED LEDGER (Edward 2026-06-26): the bottom green/red split collapses to
  // the canonical CanonicalEffectChip(s) — glyph + resource word by default, hover
  // reveals ONLY the number+magnitude. Shared renderer for the drug tooltip body AND
  // the keystone sections. When a card has an authored ledger we render these chips
  // and SUPPRESS the legacy verbose htag rows; no ledger -> fall back to the htags.
  const renderLedgerRow = (rows, keyPrefix) => (rows && rows.length > 0) ? (
    <div className="pill-card__hover-tags pill-card__hover-tags--ledger">
      {rows.map((chip, i) => (
        <CanonicalEffectChip
          key={`${keyPrefix}${i}`}
          tone={chip.tone}
          word={chip.word}
          detail={chip.detail}
          accent={chip.accent}
          signature={chip.signature}
        />
      ))}
    </div>
  ) : null;
  // Drug-card ledger: looked up by generic_name from the canonical cardLedgers map
  // (the backend reservoir effect carries no ledger of its own). A backend-authored
  // effect.ledger, if it ever exists, wins. null -> detailBody keeps the legacy htags.
  const drugLedger = effect?.ledger || getCardLedger(card?.generic_name);
  const drugLedgerRow = renderLedgerRow(drugLedger, 'dl');

  // The "what it does" detail body — description, pros/cons, mechanics, the
  // unlock-requirement bar, and the keystone-synergy bonus. Shared by the
  // in-card hover panel AND the standalone tooltip-only render below so both
  // read identically.
  const showEmptyDetail = (sideDetails || tooltipOnly)
    && !effect?.description && pros.length === 0
    && filteredCons.length === 0 && mechanics.length === 0
    && authoredInfluenceRows.length === 0;
  const detailBody = (
    <>
      {effect?.description && (
        <p className="pill-card__hover-desc">{renderStatusText(effect.description)}</p>
      )}
      {authoredInfluenceBlock}
      {showEmptyDetail && (
        <p className="pill-card__hover-desc pill-card__hover-desc--muted">
          No effect details recorded for this card yet.
        </p>
      )}
      {/* Consolidated canonical ledger supersedes the legacy verbose pros/cons +
          mechanics htag rows when this drug has an authored ledger; un-migrated
          cards keep the legacy chips so the migration is incremental and safe. */}
      {drugLedgerRow || (
        <>
          {(pros.length > 0 || filteredCons.length > 0) && (
            <div className="pill-card__hover-tags">
              {pros.map((p, i) => (
                <span key={`p${i}`} className="pill-card__htag pill-card__htag--pro">{p}</span>
              ))}
              {filteredCons.map((c, i) => (
                <span key={`c${i}`} className="pill-card__htag pill-card__htag--con">{c}</span>
              ))}
            </div>
          )}
          {mechanics.length > 0 && (
            <div className="pill-card__hover-tags">
              {mechanics.map(m => (
                <span key={m} className="pill-card__htag pill-card__htag--mech">{m}</span>
              ))}
            </div>
          )}
        </>
      )}
      {requirement && !requirement.earned && (
        <div className="pill-card__hover-req">
          <MechGlyph g="lock" size={10} />
          <span className="pill-card__hover-req-name">{renderStatusText(requirement.name, { iconSize: 9 })}</span>
          <div className="pill-card__hover-req-bar">
            <div
              className="pill-card__hover-req-fill"
              style={{ width: `${Math.min(100, Math.round((requirement.progress / requirement.threshold) * 100))}%` }}
            />
          </div>
          <span className="pill-card__hover-req-label">{requirement.progress}/{requirement.threshold}</span>
        </div>
      )}
      {keystoneBonus && (
        <div
          className={`pill-card__side-bonus${keystoneBonus.active ? ' pill-card__side-bonus--active' : ''}`}
          style={{ '--bonus-color': keystoneBonus.color }}
        >
          <div className="pill-card__side-bonus-head">
            <span className="pill-card__side-bonus-label">
              {renderStatusText(`${keystoneBonus.keystone} synergy`)}
            </span>
            <span className="pill-card__side-bonus-state">
              {keystoneBonus.active ? 'Active' : 'Equip to activate'}
            </span>
          </div>
          {keystoneBonus.lines.map((line, i) => (
            <p key={i} className="pill-card__side-bonus-line">{renderStatusText(line)}</p>
          ))}
        </div>
      )}
    </>
  );

  // The green/red pro/con tags. For keystone tooltips these are RESERVED for
  // the bottom "game effects" section only (Edward 2026-06-26); the regular
  // single-section detailBody renders its own copy inline above.
  const gameTags = (pros.length > 0 || filteredCons.length > 0) ? (
    <div className="pill-card__hover-tags">
      {pros.map((p, i) => (
        <span key={`p${i}`} className="pill-card__htag pill-card__htag--pro">{p}</span>
      ))}
      {filteredCons.map((c, i) => (
        <span key={`c${i}`} className="pill-card__htag pill-card__htag--con">{c}</span>
      ))}
    </div>
  ) : null;

  // Two-section keystone tooltip body. For Metformin the two sections map to its
  // two DISTINCT mechanics (verified against the engine 2026-06-26):
  //   TOP  "How the beats work" = the guideline beat -> a flat +10 HP Good Outcome
  //        (no boss damage, no lactic) -> KeystoneBeatAnimation.
  //   BOT  "The Heal action"     = the optional green Heal button -> drill a card
  //        -> +5 HP, +1 lactic (the lactic lever) -> KeystoneHealAnimation.
  // Other keystones (vancomycin/adalimumab) keep the generic "Game effects" label
  // and their static <p> bodies (no animations), unchanged. renderStatusText keeps
  // the lactic status glyph styling inside whichever section names it.
  const isMetforminKeystone = effect?.id === 'keystone_metformin';
  const beatsBody = effect?.beatsBody || effect?.description || '';
  const effectsBody = effect?.effectsBody || '';
  // Per-section mechanic-ledger chips. beatGains/beatCosts are the green/red chips
  // for Section 1 (the beat); pros/filteredCons (via gameTags) ride Section 2 (the
  // Heal action). Same green/red htag styling; just split so each section's chips
  // describe its own mechanic. Absent (empty) for keystones that have not authored
  // a split, so they render no Section-1 chip row.
  const beatGains = effect?.beatGains || [];
  const beatCosts = effect?.beatCosts || [];
  const beatTags = (beatGains.length > 0 || beatCosts.length > 0) ? (
    <div className="pill-card__hover-tags">
      {beatGains.map((p, i) => (
        <span key={`bg${i}`} className="pill-card__htag pill-card__htag--pro">{p}</span>
      ))}
      {beatCosts.map((c, i) => (
        <span key={`bc${i}`} className="pill-card__htag pill-card__htag--con">{c}</span>
      ))}
    </div>
  ) : null;

  // Per-section ledger rows + the section model. beatLedger -> Section 1 (the beat
  // / ability), healLedger -> Section 2 (the effect). singleSection collapses to one
  // section (Vancomycin = one effect, the crit) so the tooltip is a single column
  // like the drug tooltips; eyebrows are per-keystone (effect.beatsLabel/effectsLabel).
  const beatLedger = effect?.beatLedger || [];
  const healLedger = effect?.healLedger || [];
  const beatLedgerRow = renderLedgerRow(beatLedger, 'bl');
  const healLedgerRow = renderLedgerRow(healLedger, 'hl');
  const singleSection = !!effect?.singleSection;
  const beatsLabel = effect?.beatsLabel || 'How the beats work';
  const effectsLabel = effect?.effectsLabel || (isMetforminKeystone ? 'The Heal action' : 'Game effects');
  const beatSection = (
    <div className="pill-tooltip__section">
      <span className="pill-card__side-eyebrow">{beatsLabel}</span>
      {/* Metformin's keystone beats ARE guideline MCQs, so the top section
          shows a faithful miniature of the real question card resolving to its
          +10 HP Good Outcome. The synthetic keystone effect has no beatsType
          field, so we gate on its effect.id (built as `keystone_<id>` in
          SkillTreeNodeToast.jsx); this confines the animation to metformin and
          leaves vancomycin/adalimumab on the static body + ledger chips. */}
      {isMetforminKeystone && <KeystoneBeatAnimation />}
      {beatsBody && (
        <p className="pill-card__hover-desc">{renderStatusText(beatsBody)}</p>
      )}
      {beatLedgerRow || beatTags}
    </div>
  );
  const effectSection = (
    <div className="pill-tooltip__section pill-tooltip__section--effects">
      <span className="pill-card__side-eyebrow">{effectsLabel}</span>
      {/* Metformin's defining lever is the PRN Heal action, NOT the beat. The
          heal demo leads this section; the green/red tags + mechanics stay below
          it as the at-a-glance summary. */}
      {isMetforminKeystone && <KeystoneHealAnimation />}
      {effectsBody && (
        <p className="pill-card__hover-desc">{renderStatusText(effectsBody)}</p>
      )}
      {/* Consolidated heal ledger supersedes the verbose gameTags + mechanics rows
          when authored; non-migrated keystones keep their legacy chips. */}
      {healLedgerRow || (
        <>
          {gameTags}
          {mechanics.length > 0 && (
            <div className="pill-card__hover-tags">
              {mechanics.map(m => (
                <span key={m} className="pill-card__htag pill-card__htag--mech">{m}</span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
  const keystoneSectionsBody = singleSection ? (
    // One effect -> one section, single column (like the drug tooltips).
    beatSection
  ) : (
    // Two sections laid SIDE BY SIDE (a 2-column grid at the widened tooltip width)
    // so the panel's HEIGHT stays inside the map viewport: stacked, the two sections
    // summed past the map box; side by side the height is max(section), not sum.
    <div className="pill-tooltip__keystone-grid">
      {beatSection}
      {effectSection}
    </div>
  );
  // Keystone body for the movable side-details float (map + equip-hotbar click):
  // the SAME two sections as the tooltip, but STACKED (.ks-split) inside the drug
  // node-toast chrome, with the drug-class relocated into section 1's head to match
  // the keystone-detail export. The live beat/heal animations ride along (they live
  // inside these sections). Vancomycin collapses to one column (singleSection).
  const keystoneSideDetailsBody = (
    <div className="ks-split">
      <div className="ks-col">
        <div className="pill-card__side-head">
          <span className="pill-card__side-eyebrow">{beatsLabel}</span>
          {drugClass && <span className="pill-card__drug-class">{drugClass}</span>}
        </div>
        {isMetforminKeystone && <KeystoneBeatAnimation />}
        {beatsBody && (
          <p className="pill-card__hover-desc">{renderStatusText(beatsBody)}</p>
        )}
        {beatLedgerRow || beatTags}
      </div>
      {!singleSection && (
        <div className="ks-col">
          <div className="pill-card__side-head">
            <span className="pill-card__side-eyebrow">{effectsLabel}</span>
          </div>
          {isMetforminKeystone && <KeystoneHealAnimation />}
          {effectsBody && (
            <p className="pill-card__hover-desc">{renderStatusText(effectsBody)}</p>
          )}
          {healLedgerRow || (
            <>
              {gameTags}
              {mechanics.length > 0 && (
                <div className="pill-card__hover-tags">
                  {mechanics.map(m => (
                    <span key={m} className="pill-card__htag pill-card__htag--mech">{m}</span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );

  // Tooltip-only surface (equip hotbar): render JUST the detail panel as a
  // standalone tooltip — no card face, no foot, no action button.
  if (tooltipOnly) {
    const tipIcon = iconOverride || drugNodeGlyph(card) || (effect && getEffectIcon(effect.id));
    return (
      <div className="pill-tooltip" style={rootStyle} role="tooltip">
        <div className="pill-tooltip__head">
          {tipIcon && <span className="pill-tooltip__icon">{tipIcon}</span>}
          <span className="pill-tooltip__titles">
            {/* Two-section keystones carry their own section labels, so the
                header eyebrow is dropped to avoid a triple-eyebrow stack. */}
            {!keystoneSections && (
              <span className="pill-card__side-eyebrow">What it does</span>
            )}
            <span className="pill-card__side-title">{effect?.name || card.generic_name}</span>
            {(card.generic_name || drugClass) && (
              <span className="pill-tooltip__sub">
                {[card.generic_name, drugClass].filter(Boolean).join(' · ')}
              </span>
            )}
          </span>
        </div>
        <div className="pill-tooltip__body">
          {keystoneSections ? keystoneSectionsBody : detailBody}
        </div>
      </div>
    );
  }

  // ── Reconciled float layout helpers (design port) ──────────────────────
  // A clickable header lens handle. Interactive only when a drug lens exists
  // (lensEnabled) so a card with no clinical copy shows no dead affordance.
  const renderLensHandle = (which, text, cls) => (
    <div
      className={cls}
      {...(lensEnabled ? {
        role: 'button',
        tabIndex: 0,
        onClick: (e) => { e.stopPropagation(); onLensSelect?.(which); },
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onLensSelect?.(which); }
        },
      } : {})}
    >
      {text}
    </div>
  );

  // Synergy-name lift: the design pulls a leading "NAME:" token off the first
  // synergy line into its own header span (e.g. "BETA-BLOCKADE"), leaving the
  // rest as the body. Falls back to the keystone display name when the line has
  // no leading token.
  const synergyNameMatch = keystoneBonus?.lines?.[0]?.match?.(/^\s*([A-Z][A-Z0-9 /-]{1,28}):\s*/);
  const synergyName = keystoneBonus
    ? (synergyNameMatch ? synergyNameMatch[1].trim() : (keystoneBonus.name || keystoneBonus.keystone || ''))
    : '';
  const synergyLines = (keystoneBonus?.lines || []).map(
    (ln, i) => (i === 0 && synergyNameMatch) ? ln.slice(synergyNameMatch[0].length) : ln,
  );
  // Dynamic synergy glyph: the "⋃ synergy" label's leading mark reflects WHICH
  // keystone this synergy pairs with (there are exactly 3 — vancomycin /
  // metformin / adalimumab — each with its own icon path), tinted with the
  // keystone accent the block already carries. Falls back to the generic union
  // mark if a path ever fails to resolve. (Edward 2026-07-10: the union svg was
  // static; it should be the keystone-specific mark.)
  const synergyGlyphPath = keystoneBonus?.keystoneSlug
    ? getKeystoneIconPath(keystoneBonus.keystoneSlug)
    : null;

  // The reconciled detail body: lens-aware description, ledger chips (CSS hides
  // them in the drug lens), the lifted synergy block, and the achievement row.
  const layoutBody = (
    <>
      {lens === 'drug' && drugLensDesc ? (
        <p className="pill-card__hover-desc">{drugLensDesc}</p>
      ) : effect?.description && !isHtnDrugToast ? (
        <p className="pill-card__hover-desc">{renderStatusText(effect.description)}</p>
      ) : null}
      {lens === 'effect' && authoredInfluenceBlock}
      {!isHtnDrugToast && (drugLedgerRow || (
        (pros.length > 0 || filteredCons.length > 0) ? (
          <div className="pill-card__hover-tags">
            {pros.map((p, i) => (
              <span key={`p${i}`} className="pill-card__htag pill-card__htag--pro">{p}</span>
            ))}
            {filteredCons.map((c, i) => (
              <span key={`c${i}`} className="pill-card__htag pill-card__htag--con">{c}</span>
            ))}
          </div>
        ) : null
      ))}
      {keystoneBonus && (
        <div
          className={`pill-card__side-bonus${keystoneBonus.active ? ' pill-card__side-bonus--active' : ''}`}
          style={{ '--bonus-color': keystoneBonus.color }}
        >
          <div className="pill-card__side-bonus-head">
            {synergyName && <span className="pill-card__side-bonus-name">{synergyName}</span>}
            <span className="pill-card__side-bonus-label">
              {synergyGlyphPath ? (
                <svg
                  className="pill-card__side-bonus-glyph"
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.9}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  style={{ color: keystoneBonus.color, verticalAlign: '-2px', marginRight: '3px' }}
                >
                  <path d={synergyGlyphPath} />
                </svg>
              ) : (
                <span aria-hidden="true" style={{ marginRight: '3px' }}>⋃</span>
              )}
              synergy
            </span>
            <span className="pill-card__side-bonus-state">
              {keystoneBonus.active ? 'Active' : 'Equip to activate'}
            </span>
          </div>
          {synergyLines.map((line, i) => (
            <p key={i} className="pill-card__side-bonus-line">{renderStatusText(line)}</p>
          ))}
        </div>
      )}
      {requirement && !requirement.earned && (
        <div className="pill-card__hover-req">
          <MechGlyph g="lock" size={10} />
          <span className="pill-card__hover-req-name">{renderStatusText(requirement.name, { iconSize: 9 })}</span>
          <div className="pill-card__hover-req-bar">
            <div
              className="pill-card__hover-req-fill"
              style={{ width: `${Math.min(100, Math.round((requirement.progress / requirement.threshold) * 100))}%` }}
            />
          </div>
          <span className="pill-card__hover-req-label">{requirement.progress}/{requirement.threshold}</span>
        </div>
      )}
    </>
  );

  return (
    <div className={classes} style={rootStyle} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>

      {/* Status badge */}
      {equipped && <span className="pill-card__badge pill-card__badge--equipped">Equipped</span>}
      {!equipped && owned && <span className="pill-card__badge pill-card__badge--owned">Owned</span>}
      {!equipped && !owned && card.is_starter && <span className="pill-card__badge pill-card__badge--starter">Starter</span>}

      {/* Content area — face swaps with hover details.
          The reconciled float layout (design port) emits a split header +
          relocated class + centered body statically; every other surface keeps
          the classic icon-ring face + hover swap. */}
      <div className="pill-card__content">
        {sideDetailsLayout ? (
          <>
            {/* ── Split header: effect │ generic (both are dual-lens handles) ── */}
            <div className="pill-card__face">
              <div className="pill-card__headrow">
                {renderLensHandle('effect', effect?.name || 'Unknown', 'pill-card__ability')}
                {renderLensHandle('drug', card.generic_name, 'pill-card__drug-name')}
              </div>
            </div>
            {/* ── Body: keystones stack their two sections (.ks-split, with the
                 live beat/heal animations); drugs get the eyebrow + relocated
                 drug-class + single detail body. ── */}
            <div className="pill-card__hover">
              {keystoneSections ? keystoneSideDetailsBody : (
                <>
                  <div className="pill-card__side-head">
                    <span className="pill-card__side-eyebrow">What it does</span>
                    {!isHtnDrugToast && (
                      <span className="pill-card__side-title">{effect?.name || card.generic_name}</span>
                    )}
                    {drugClass && <span className="pill-card__drug-class">{drugClass}</span>}
                  </div>
                  {layoutBody}
                </>
              )}
            </div>
          </>
        ) : (
          <>
            {/* ── FACE (hidden on hover) ── */}
            <div className="pill-card__face">
              <div className="pill-card__icon-ring">
                {iconOverride || drugNodeGlyph(card) || (effect && getEffectIcon(effect.id))}
              </div>
              <div className="pill-card__ability">{effect?.name || 'Unknown'}</div>
              <div className="pill-card__drug-name">{card.generic_name}</div>
              {drugClass && <div className="pill-card__drug-class">{drugClass}</div>}
            </div>

            {/* ── HOVER DETAILS (shown on hover; side panel when sideDetails) ── */}
            <div className="pill-card__hover">
              {sideDetails && (
                <div className="pill-card__side-head">
                  <span className="pill-card__side-eyebrow">What it does</span>
                  <span className="pill-card__side-title">{effect?.name || card.generic_name}</span>
                </div>
              )}
              {detailBody}
            </div>
          </>
        )}
      </div>

      {/* Footer. Reconciled layout: the 4 math-glyph action buttons (built by
          the toast). Classic layout: type tag + inline foot action + cost. */}
      <div className="pill-card__foot">
        {sideDetailsLayout ? footButtons : (
          <>
            {!hideType && <span className={`pill-card__type pill-card__type--${typeClass}`}>{typeText}</span>}
            {footAction && <span className="pill-card__foot-action">{footAction}</span>}
            {showCost && (
              <span className={`pill-card__cost${card.cost === 0 ? ' pill-card__cost--free' : ''}`}>
                {card.cost === 0 ? 'Free' : `$${card.cost}`}
              </span>
            )}
          </>
        )}
      </div>

      {/* Action button — always accessible, never overlaid */}
      {actionButton && <div className="pill-card__action">{actionButton}</div>}
    </div>
  );
}
